import { PersonRow } from '@affine/core/components/identity-row';
import { PublicUserService } from '@affine/core/modules/cloud';
import { useLiveData, useService } from '@toeverything/infra';
import { type CSSProperties, type ReactNode, useEffect } from 'react';

import * as styles from './styles.css';

export type MemberItemVariant = 'inline' | 'list' | 'row';

/** User data that can be passed directly instead of loading from userId */
export interface MemberItemUser {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string | null;
}

export interface MemberItemProps {
  /** User ID to load user data from PublicUserService */
  userId?: string;
  /** User data passed directly (alternative to userId) */
  user?: MemberItemUser;
  idx?: number;
  maxWidth?: number | string;
  focused?: boolean;
  onRemove?: () => void;
  style?: CSSProperties;
  /** Display variant: 'inline' (tag-like), 'list' (simple), 'row' (full with email/role) */
  variant?: MemberItemVariant;
  /** Slot for role selector/dropdown (only used in 'row' variant) */
  roleSelector?: ReactNode;
  /** Show tooltips on name/email (only used in 'row' variant) */
  showTooltips?: boolean;
}

export const MemberItem = ({
  userId,
  user: userProp,
  idx,
  focused,
  onRemove,
  style,
  maxWidth,
  variant = 'inline',
  roleSelector,
  showTooltips = false,
}: MemberItemProps) => {
  const publicUserService = useService(PublicUserService);
  const loadedMember = useLiveData(
    userId ? publicUserService.publicUser$(userId) : null
  );
  const isLoading = useLiveData(
    userId ? publicUserService.isLoading$(userId) : null
  );
  useEffect(() => {
    if (userId) {
      publicUserService.revalidate(userId);
    }
  }, [userId, publicUserService]);

  // Use passed user data or loaded member data
  const member = userProp ?? loadedMember;

  const identityVariant = variant === 'row' ? 'row' : 'pill';
  const removeTestId =
    variant === 'row' ? 'remove-member-button' : 'remove-tag-button';

  // Loading/removed state
  if (!member || ('removed' in member && member.removed)) {
    const removed = Boolean(member && 'removed' in member && member.removed);
    const fallbackPerson = {
      id: userId,
      name: removed ? 'Removed user' : undefined,
    };

    if (variant === 'row') {
      return (
        <PersonRow
          person={fallbackPerson}
          variant="row"
          loading={!removed}
          style={style}
        />
      );
    }
    return (
      <div className={styles.memberItem} data-idx={idx} style={style}>
        <div
          style={{ maxWidth: maxWidth }}
          data-focused={focused}
          className={styles.memberItemInlineMode}
        >
          <PersonRow
            person={fallbackPerson}
            variant="pill"
            loading={!removed && isLoading !== false}
            onRemove={onRemove}
            removeTestId={removeTestId}
          />
        </div>
      </div>
    );
  }

  const { name, avatarUrl } = member;
  // email only exists on MemberItemUser, not on ExistedUserInfo
  const email = 'email' in member ? member.email : undefined;

  if (variant === 'row') {
    return (
      <PersonRow
        person={{
          id: member.id,
          name,
          email,
          avatarUrl,
        }}
        variant="row"
        showTooltips={showTooltips}
        actions={roleSelector}
        onRemove={onRemove}
        removeTestId={removeTestId}
        style={style}
      />
    );
  }

  return (
    <div
      className={styles.memberItem}
      data-idx={idx}
      title={name ?? undefined}
      style={style}
    >
      <div
        style={{ maxWidth: maxWidth }}
        data-focused={focused}
        className={styles.memberItemInlineMode}
      >
        <PersonRow
          person={{
            id: member.id,
            name,
            email,
            avatarUrl,
          }}
          variant={identityVariant}
          focused={focused}
          onRemove={onRemove}
          removeTestId={removeTestId}
          maxWidth={maxWidth}
        />
      </div>
    </div>
  );
};
